/**
 * route-access.ts — garde de route par rôle (côté client).
 *
 * Le menu (admin-shell.tsx) masque déjà les sections interdites au rôle
 * courant, mais une URL tapée directement rendait quand même la page
 * (seul le backend répondait 403). Ce module associe chaque route admin
 * à sa section de menu pour que App.tsx puisse rendre une page 403
 * honnête (pages/forbidden.tsx) au lieu du contenu.
 *
 * IMPORTANT : le découpage (section, adminOnly) doit rester synchronisé
 * avec `adminSections` de admin-shell.tsx — un test unitaire le vérifie
 * (client/src/__tests__/route-access.test.tsx).
 *
 * NB : ceci reste du confort UX, pas de la sécurité — la vraie
 * autorisation est faite par la gateway/les services (JWT + rôles).
 */

import { ROLE_SECTIONS, isAdminRole, type UserRole } from '@/lib/auth';

export interface RouteAccessRule {
  /** Préfixe de route (matché exact ou suivi de `/`). */
  prefix: string;
  /** Id de section du menu (cf. adminSections[].id dans admin-shell.tsx). */
  section: string;
  /** Réservé aux rôles pleinement admin (cf. isAdminRole). */
  adminOnly?: boolean;
}

export const ROUTE_ACCESS: RouteAccessRule[] = [
  // Contenu
  { prefix: '/admin/dashboard-diocese', section: 'content' },
  { prefix: '/admin/statistiques', section: 'content' },
  { prefix: '/admin/mediatheque', section: 'content' },
  { prefix: '/admin/messages', section: 'content' },
  { prefix: '/admin/support', section: 'content' }, // console Support (26/10), comme son entrée de menu
  { prefix: '/admin/actualites', section: 'content' }, // couvre aussi /editeur/:id?
  { prefix: '/admin/videos', section: 'content' },
  { prefix: '/admin/evenements', section: 'content' },
  { prefix: '/admin/informations-urgentes', section: 'content' },
  { prefix: '/admin/documents', section: 'content' },
  { prefix: '/admin/espace-pretres', section: 'content' },
  { prefix: '/admin/newsletter', section: 'content' },
  { prefix: '/admin/calendrier-editorial', section: 'content' },
  { prefix: '/admin/contributions', section: 'content', adminOnly: true },

  // Administration
  { prefix: '/admin/utilisateurs', section: 'admin' },
  { prefix: '/admin/roles', section: 'admin' },
  { prefix: '/admin/parametres', section: 'admin' },
  { prefix: '/admin/feature-flags', section: 'admin' },
  { prefix: '/admin/campagne-applications', section: 'admin' },
  { prefix: '/admin/audit', section: 'admin' },
  { prefix: '/admin/sms', section: 'admin' },
  { prefix: '/admin/comptes-espace-pretres', section: 'admin', adminOnly: true },
  { prefix: '/admin/site-edaci', section: 'admin', adminOnly: true },
  { prefix: '/admin/textes-pages', section: 'admin', adminOnly: true },
  { prefix: '/admin/applications', section: 'admin', adminOnly: true },
  { prefix: '/admin/notifications-push', section: 'content' },

  // Territoire
  { prefix: '/admin/vicariats', section: 'territory' },
  { prefix: '/admin/doyennes', section: 'territory' },

  // Missions
  { prefix: '/admin/pelerinages', section: 'missions' },
  { prefix: '/admin/collectes', section: 'missions' },
  { prefix: '/admin/solidarite-projets', section: 'missions' },

  // Doctrine
  { prefix: '/admin/contenu-doctrinal', section: 'doctrine' },
];

/** Page d'accueil historique (section "content"). */
export const DEFAULT_HOME = '/admin/dashboard-diocese';

/** Règle d'accès applicable à un chemin (match exact ou par préfixe). */
export function findRouteAccess(path: string): RouteAccessRule | undefined {
  return ROUTE_ACCESS.find(
    (rule) => path === rule.prefix || path.startsWith(`${rule.prefix}/`),
  );
}

/**
 * Le rôle courant peut-il afficher cette route ?
 *  - route inconnue → true (le Switch retombe sur le dashboard) ;
 *  - pas de rôle (non connecté) → true (RouteGuard redirige déjà vers "/").
 */
export function canAccessPath(
  path: string,
  role: UserRole | undefined | null,
): boolean {
  const rule = findRouteAccess(path);
  if (!rule || !role) return true;
  if (rule.adminOnly && !isAdminRole(role)) return false;
  return ROLE_SECTIONS[role]?.includes(rule.section) ?? false;
}

/**
 * Première page autorisée au rôle — destination après connexion, cible du
 * lien de repli de la page 403, et route par défaut du Switch.
 *
 * Sans cela, un membre de la commission doctrinale (rôle `doctrine`, qui n'a
 * pas la section "content") était renvoyé après login sur
 * /admin/dashboard-diocese, c'est-à-dire sur un « Accès refusé » — connexion
 * réussie, écran d'erreur. On tombe donc sur la première route que son rôle
 * peut réellement afficher.
 */
export function homePathFor(role: UserRole | undefined | null): string {
  if (!role) return DEFAULT_HOME;
  if (canAccessPath(DEFAULT_HOME, role)) return DEFAULT_HOME;
  const first = ROUTE_ACCESS.find((rule) => canAccessPath(rule.prefix, role));
  return first ? first.prefix : DEFAULT_HOME;
}
